/**
 * Wave spawn position modes, as serialised in Song Pack v1 `.chart` files.
 *
 * The numeric values are fixed by the producer side and MUST NOT be reordered:
 * they are the ordinal values of Unity's `WaveChartDefinition.SpawnPositionMode`
 * enum, which `JsonUtility` writes as plain integers. The Python engine emits the
 * same integers directly (`rule_based.py` uses 1, 2 and 3).
 *
 * This module is data-only. It knows nothing about how a mode is resolved into a
 * world position; that is the simulation layer's concern.
 */

export const SpawnPositionMode = {
  /** Use `fixedWorldPosition`, falling back to `origin`. */
  FixedWorldPosition: 0,
  /** Map `normalizedPosition` (0..1 per axis) across the play field extent. */
  NormalizedPosition: 1,
  /** Random point inside the play field, honouring `edgePadding`. */
  RandomInsidePlayArea: 2,
  /** Random point on the play field boundary, honouring `edgePadding`. */
  RandomOnEdge: 3,
  /** The player's position at the moment the wave spawns. */
  PlayerPositionAtSpawn: 4,
} as const;

export type SpawnPositionMode = (typeof SpawnPositionMode)[keyof typeof SpawnPositionMode];

/** Lowest and highest valid ordinal, inclusive. */
export const SPAWN_POSITION_MODE_MIN = 0;
export const SPAWN_POSITION_MODE_MAX = 4;

const NAMES: Readonly<Record<SpawnPositionMode, string>> = {
  [SpawnPositionMode.FixedWorldPosition]: 'FixedWorldPosition',
  [SpawnPositionMode.NormalizedPosition]: 'NormalizedPosition',
  [SpawnPositionMode.RandomInsidePlayArea]: 'RandomInsidePlayArea',
  [SpawnPositionMode.RandomOnEdge]: 'RandomOnEdge',
  [SpawnPositionMode.PlayerPositionAtSpawn]: 'PlayerPositionAtSpawn',
};

export function isSpawnPositionMode(value: unknown): value is SpawnPositionMode {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= SPAWN_POSITION_MODE_MIN &&
    value <= SPAWN_POSITION_MODE_MAX
  );
}

/** Human-readable name, for diagnostics only. Never used as a serialised value. */
export function spawnPositionModeName(mode: SpawnPositionMode): string {
  return NAMES[mode];
}
